import React, { ReactElement, useCallback, useEffect, useRef, useState } from 'react';

import { Button, ButtonDescription, Column, GridContainer, H3, H5, Icon, Row, Spinner, Typography } from '@vp/swan';
import { ValidationState } from '@vp/upload-and-print-validator';
import { LOCALIZED_URLS } from 'src/constants';
import { useErrorContext } from 'src/hooks/ErrorContextHooks';
import { useProductContext } from 'src/hooks/ProductContextHooks';
import { useStudioContext } from 'src/hooks/StudioContextHooks';
import { useTrackingContext } from 'src/hooks/TrackingContextHooks';
import { useProcessUpload, useUploadContext } from 'src/hooks/UploadContextHooks';
import { useIsPdfOnly, useValidationContext } from 'src/hooks/ValidationContextHooks';
import { useWindowSize } from 'src/hooks/WindowSizeHooks';
import { createWork } from 'src/services/UploadAndPrintService';
import { getLanguage, text } from 'src/utils/localizationUtil';
import { filterRecommendedProductOptionsToJustProductPageOptions } from 'src/utils/productOptionUtils';
import QueryStringUtil from 'src/utils/QueryStringUtil';
import { createTrackingDataForProduct } from 'src/utils/trackingUtil';
import { hasValidationErrors } from 'src/utils/validationUtil';
import { FileRequirements } from './FileRequirements';
import { PreUploadedFileProcessor } from './PreUploadedFileProcessor';
import Previewer from './Previewer';
import PricingInformation from './PricingInformation';
import { ProductOptionChangesPrompt } from './ProductOptionChangesPrompt';
import Uploader from './Uploader';
import { AnalyticsEventData, sendEventTracking } from '../Analytics';

export type UploadAndPrintFormProps = {
  isMobile: boolean;
};

const UploadAndPrintForm = ({ isMobile }: UploadAndPrintFormProps): ReactElement => {
  const { upload, setUpload, isPreUploaded } = useUploadContext();
  const { productInformation, setProductInformation, completedProductInformation } = useProductContext();
  const { reprocessUpload } = useProcessUpload();
  const { setError } = useErrorContext();
  const { isValidating, validations, needsRevalidation, setValidations, setIsValidating } = useValidationContext();
  const { isStudioInitialized, studioUrl } = useStudioContext();
  const { trackingConfiguration } = useTrackingContext();
  const [orderSubmitting, setOrderSubmitting] = useState(false);
  const [productOptionChangePromptOpen, setProductOptionChangePromptOpen] = useState(false);
  const minimumPreviewHeight = 100;
  const fullSizeButtonHeight = 220;
  const defaultPreviewHeight = 500;
  const mobilePreviewHeight = 500;
  const previewRowRef = useRef<HTMLElement>(null);
  const [previewRowHeight, setPreviewRowHeight] = useState(defaultPreviewHeight);
  const windowSize = useWindowSize();
  const lng = getLanguage();
  const printGuidelinesPDFURL = LOCALIZED_URLS[lng];

  // This is not the best logic to determine if this is a booklets product, but it should work for now
  const isBooklet = useIsPdfOnly();

  //These are split out from Upload in order to have more granular dependencies in the below useEffect
  //If that useEffect has a direct dependency on Upload, it will re-trigger whenever Upload changes based on
  //a reference equality check, and becuase sendToPrepress updates the upload object, that will trigger an infinite
  //loop where this useEffect retriggers itself again and again.
  const uploadFileUri = upload?.fileUri ?? '';
  const uploadFileName = upload?.uploadedFileName ?? '';
  const uploadThumbnailUri = upload?.thumbnailUrl ?? '';

  useEffect(() => {
    if (previewRowRef !== null) {
      setPreviewRowHeight(previewRowRef.current?.getBoundingClientRect().height ?? defaultPreviewHeight);
    }
  }, [windowSize.width, windowSize.height]);

  useEffect(() => {
    const applyProductOptionRecommendations = async () => {
      if (productInformation.recommendedProductData !== undefined) {
        let recommendationsAreDifferentFromCurrentSelection = false;

        const productOptionsToAccept = await filterRecommendedProductOptionsToJustProductPageOptions(
          productInformation.productKey,
          productInformation.productVersion,
          productInformation.recommendedProductData.productOptions
        );

        Object.entries(productOptionsToAccept).forEach(([key, value]) => {
          if (
            (productInformation.selectedOptions[key] && productInformation.selectedOptions[key] !== value) ||
            (completedProductInformation?.selectedOptions[key] &&
              completedProductInformation.selectedOptions[key] !== value)
          ) {
            recommendationsAreDifferentFromCurrentSelection = true;
          }
        });

        setProductOptionChangePromptOpen(recommendationsAreDifferentFromCurrentSelection);
      } else {
        setProductOptionChangePromptOpen(false);
      }
    };

    applyProductOptionRecommendations();
  }, [
    productInformation.recommendedProductData,
    productInformation.selectedOptions,
    productInformation.mpvId,
    productInformation.productKey,
    productInformation.productName,
    productInformation.productVersion,
    productInformation.quantity,
    uploadFileUri,
    uploadFileName,
    uploadThumbnailUri,
    completedProductInformation,
    setIsValidating,
    setProductInformation,
    setUpload,
    setValidations,
  ]);

  const formIsValid = useCallback(() => {
    return (
      (upload?.processedUploadUri && !isValidating && !hasValidationErrors(validations)) ||
      (upload?.fileUri && needsRevalidation)
    );
  }, [upload, isValidating, validations, needsRevalidation]);

  const submitForm = useCallback(
    async (customizeDesign: boolean) => {
      try {
        if (!upload || (!upload.processedUploadUri && !needsRevalidation)) {
          setError(new Error('Tried to checkout without an upload or without a processedUploadUri'));
          return;
        }

        setOrderSubmitting(true);

        let uploadToUse = upload;

        if (!customizeDesign) {
          let hasRevalidationErrors = false;

          // Revalidate if we don't have validations or something has not been validated.
          // This is typically caused by a page refresh, or by a customer changing their product option selection
          // after validations have run
          if (
            !validations ||
            validations.filter(v => v.state === ValidationState.UnValidated).length > 0 ||
            needsRevalidation
          ) {
            const reprocessResult = reprocessUpload();

            if (!reprocessResult) {
              setError(new Error('Error reprocessing upload'));
              return;
            }

            const awaitedResult = await reprocessResult;

            if (!awaitedResult) {
              setError(new Error('Error awaiting results from the reprocessed upload'));
              return;
            }

            const { newUpload, processUploadResponse } = awaitedResult;

            if (!newUpload.processedUploadUri) {
              setError(new Error('Reprocessed upload is missing a processedUploadUri'));
              return;
            }

            uploadToUse = newUpload;

            // The formIsValid check won't catch any errors that occur during revalidation,
            // because setState calls don't modify the state until the next render, so
            // the formIsValid check will be looking at the old validation state
            processUploadResponse.validations.forEach(validation => {
              if (validation.state === 'Error' || validation.state === 'Warning') {
                hasRevalidationErrors = true;
              }
            });
          }

          if (!formIsValid() || hasRevalidationErrors) {
            setOrderSubmitting(false);
            return;
          }
        }

        if (!uploadToUse.processedUploadUri) {
          setError(new Error('Upload is missing a processedUploadUri'));
          return;
        }

        const editUrl = QueryStringUtil.getValueByKey('editUrl') || buildEditUrl(studioUrl);
        const workId = QueryStringUtil.getValueByKey('workId');
        const createdBy = QueryStringUtil.getValueByKey('createdBy');
        const vistaCreateProjectId = QueryStringUtil.getValueByKey('vistaCreateProjectId');

        // Add to my projects
        const { nextPageUrl, workId: newWorkId } = await createWork(
          uploadToUse.processedUploadUri,
          productInformation.productName,
          productInformation.productKey,
          productInformation.productVersion,
          productInformation.quantity,
          lng,
          workId,
          productInformation.selectedOptions,
          editUrl,
          createdBy,
          vistaCreateProjectId
        );

        // Clear upload once we are ready to redirect customer
        setUpload(null);

        // Send user to the next page in the flow
        window.location.href = customizeDesign && editUrl ? editUrl.replace(`\${workId}`, newWorkId) : nextPageUrl;
      } catch (e) {
        console.error('Error submitting form', e);
        setError(e);
      } finally {
        setOrderSubmitting(false);
      }
    },
    [
      lng,
      upload,
      productInformation,
      studioUrl,
      validations,
      setUpload,
      setOrderSubmitting,
      formIsValid,
      setError,
      needsRevalidation,
      reprocessUpload,
    ]
  );

  const openUrl = (url: string) => {
    window.open(url, '_blank');
  };

  return (
    <GridContainer>
      <Row sticky={!isMobile}>
        {(!isMobile || (isPreUploaded && upload?.renderingInstructionsUrl && completedProductInformation)) && (
          <Column
            spanLg={9}
            spanMd={8}
            span={7}
            backgroundColor="strong"
            sticky
            style={{ height: isMobile ? mobilePreviewHeight : '100vh', minHeight: minimumPreviewHeight }}
            ref={previewRowRef}
          >
            <Previewer maxHeight={Math.max(previewRowHeight - fullSizeButtonHeight, minimumPreviewHeight)} />
          </Column>
        )}
        <Column
          spanLg={3}
          spanMd={4}
          span={5}
          paddingLeft={isMobile ? '5' : '7'}
          paddingRight={isMobile ? '5' : '7'}
          paddingBottom={'7'}
        >
          <ProductOptionChangesPrompt
            isOpen={productOptionChangePromptOpen}
            setIsOpen={setProductOptionChangePromptOpen}
            isMobile={false}
          />

          <H3 paddingTop={isMobile ? '5' : '9'} paddingBottom={isMobile ? '5' : '9'} fontWeight="bold">
            {text('form-header')}
          </H3>

          {isPreUploaded ? (
            <PreUploadedFileProcessor isMobile={isMobile} />
          ) : (
            <>
              <H5>{text('upload-header')}</H5>
              <Uploader isMobile={isMobile} />
              <Typography fontSize={'small'} textColor="subtle">
                {text('upload-helper')}
              </Typography>
            </>
          )}
          <FileRequirements isMobile={isMobile} />
          {printGuidelinesPDFURL && isBooklet && (
            <Button
              width="full-width"
              specialVariant="design-path"
              marginTop={isMobile ? '5' : '7'}
              marginBottom={isMobile ? '5' : '7'}
              component="a"
              onClick={() => {
                sendEventTracking({
                  ...trackingConfiguration,
                  ...createTrackingDataForProduct(productInformation),
                  action: 'Link Clicked',
                  label: 'Print Guidelines',
                  category: 'Design - Upload and Print',
                  variant: productInformation.selectedOptions,
                } as AnalyticsEventData);
                openUrl(printGuidelinesPDFURL);
              }}
            >
              <Icon iconType="arrowRight" />
              {text('print-guidelines')}
              <ButtonDescription>{text('print-guidelines-desc')}</ButtonDescription>
            </Button>
          )}
          <PricingInformation isMobile={isMobile} />
          <Button
            skin="primary"
            size="standard"
            width="full-width"
            iconPosition="right"
            marginTop={'6'}
            onClick={e => {
              e.preventDefault();
              submitForm(false);
            }}
            disabled={!formIsValid() || orderSubmitting || !isStudioInitialized}
          >
            {orderSubmitting && <Spinner accessibleText={''} />}
            {orderSubmitting ? text('processing') : text('next-button')}
          </Button>
          {studioUrl && (
            <Button
              skin="secondary"
              size="standard"
              width="full-width"
              iconPosition="right"
              marginTop={'6'}
              {...(upload?.processedUploadUri
                ? {
                    component: 'button',
                    onClick: e => {
                      e.preventDefault();
                      submitForm(true);
                    },
                  }
                : {
                    component: 'a',
                    href: studioUrl,
                  })}
            >
              {orderSubmitting && <Spinner accessibleText={''} />}
              {orderSubmitting ? text('processing') : text('edit-button')}
            </Button>
          )}
        </Column>
      </Row>
    </GridContainer>
  );
};

function buildEditUrl(studioUrl: string | undefined): string | null {
  if (!studioUrl) {
    return null;
  }

  const path = studioUrl.substring(0, studioUrl.indexOf('?'));
  return `${path}?workId=\${workId}`;
}

export { UploadAndPrintForm };
